// @flow
import * as React from 'react';
import getDaysInMonth from 'date-fns/get_days_in_month';
import getDay from 'date-fns/get_day';
import setDate from 'date-fns/set_date';
import getDate from 'date-fns/get_date';
import startOfMonth from 'date-fns/start_of_month';
import addMonths from 'date-fns/add_months';
import subMonths from 'date-fns/sub_months';
import addDays from 'date-fns/add_days';
import format from 'date-fns/format';
import isSameDay from 'date-fns/is_same_day';
import addYears from 'date-fns/add_years';
import subYears from 'date-fns/sub_years';
import ClickOutside from 'react-click-outside';
import classnames from 'classnames';

const Days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

// type DateValue = string | Date;
type Props = {
  min?: Date,
  max?: Date,
  value: ?Date,
  onChange: () => ?Date,
};

type State = {
  isOpen: boolean,
  dateInView: Date,
};

export default class DatePicker extends React.PureComponent<Props, State> {
  state = {
    isOpen: false,
    dateInView: this.props.value || new Date(),
  };
  input: ?HTMLInputElement;
  menu: ?HTMLTableElement;

  open = () => {
    this.setState({ isOpen: true });
  };

  close = () => {
    this.setState({ isOpen: false });
  };

  setDate = (d: Date) => {
    this.setState({
      dateInView: d,
    });

    if (typeof this.props.onChange === 'function') {
      this.props.onChange(d);
    }
  };

  handleDayClick = (e: SyntheticEvent<HTMLButtonElement>) => {
    const day = Number(e.currentTarget.textContent);
    this.setDate(setDate(this.state.dateInView, day));

    e.preventDefault();
  };

  addYears = () =>
    this.setState(prevState => ({
      dateInView: addYears(prevState.dateInView, 1),
    }));

  subYears = () =>
    this.setState(prevState => ({
      dateInView: subYears(prevState.dateInView, 1),
    }));

  addMonths = () =>
    this.setState(prevState => ({
      dateInView: addMonths(prevState.dateInView, 1),
    }));

  subMonths = () =>
    this.setState(prevState => ({
      dateInView: subMonths(prevState.dateInView, 1),
    }));

  resetRange = () => {
    this.setDate(new Date());
  };

  getButtonClassName(isToday: boolean, isActive: boolean) {
    return classnames(
      'lh-solid tc ma0 w-100 br-pill b--none bg-white pointer link pa2 bg-animate',
      {
        'b bg-gold br-pill': isToday === true,
        'bg-blue white': isActive === true,
        'hover-bg-silver hover-white': isActive === false,
      }
    );
  }

  componentWillUpdate(nextProps: Props, nextState: State) {
    // update DateInView if nece
    if (nextProps.value && nextProps.value !== this.props.value) {
      this.setState({
        dateInView: nextProps.value,
      });
    }
  }

  renderHeader() {
    const { dateInView } = this.state;

    return [
      <tr key="years">
        <td className="bb b--moon-gray">
          <ArrowButton onClick={this.subYears} dir="left" />
        </td>
        <td colSpan="5" className="tc bb b--moon-gray">
          {format(dateInView, 'YYYY')}
        </td>
        <td className="bb b--moon-gray">
          <ArrowButton onClick={this.addYears} dir="right" />
        </td>
      </tr>,
      <tr key="months">
        <td className="bb b--moon-gray">
          <ArrowButton onClick={this.subMonths} dir="left" />
        </td>
        <td colSpan="5" className="tc bb b--moon-gray">
          {format(dateInView, 'MMMM')}
        </td>
        <td className="bb b--moon-gray">
          <ArrowButton onClick={this.addMonths} dir="right" />
        </td>
      </tr>,
      <tr key="days">
        {Days.map(d => (
          <th key={d} className="pa2">
            {d}
          </th>
        ))}
      </tr>,
    ];
  }

  render() {
    const { dateInView, isOpen } = this.state;
    const today = new Date();
    const { value } = this.props;
    const data = generateDates(dateInView);

    return (
      <ClickOutside onClickOutside={this.close} className="relative DatePicker">
        <input
          type="text"
          value={this.props.value ? format(this.props.value, 'MM/DD/YYYY') : ''}
          readOnly={true}
          ref={ref => (this.input = ref)}
          onClick={this.open}
          onFocus={this.open}
          className="DatePicker__input"
        />
        {isOpen === true && (
          <table
            className={
              'collapse ba br1 bg-white b--moon-gray shadow-5 z-999 DatePicker__menu'
            }
            ref={ref => {
              this.menu = ref;
              //this.input && this.input.focus();
            }}
            style={{ position: 'absolute', left: 0, marginTop: '-1px' }}
          >
            <thead>{this.renderHeader()}</thead>
            <tbody>
              {data.map((r, i) => (
                <tr key={'row' + i}>
                  {r.map((r, j) => (
                    <td key={'cell' + j} className="pa1">
                      {r ? (
                        <button
                          onClick={this.handleDayClick}
                          role="option"
                          aria-selected="true"
                          className={this.getButtonClassName(
                            isSameDay(r, today),
                            value != null ? isSameDay(r, value) : false
                          )}
                        >
                          {getDate(r)}
                        </button>
                      ) : (
                        ' '
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="7" className="ba b--moon-gray pa0">
                  <button
                    onClick={this.resetRange}
                    type="button"
                    className="input-reset button-reset db w-100 pa2 ma0 bg-near-white b--none mid-gray"
                  >
                    Today
                  </button>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </ClickOutside>
    );
  }
}

function generateDates(dt: Date): Array<Array<?Date>> {
  const dates = [];

  const firstDayOfMonth = startOfMonth(dt);
  const daysInMonth = getDaysInMonth(dt);
  const dayOfFirstOfMonth = getDay(firstDayOfMonth);

  let fdates = Array(daysInMonth)
    .fill(firstDayOfMonth)
    .map((_, index) => addDays(_, index));
  fdates.unshift(...Array(dayOfFirstOfMonth).fill(null));

  while (fdates.length > 0) {
    dates.push(fdates.slice(0, 7));
    fdates = fdates.slice(7);
  }

  // fill
  const lastRow = dates[dates.length - 1];
  while (lastRow.length < 7) {
    lastRow.push(null);
  }

  return dates;
}

function ArrowButton(props: { onClick: Function, dir: 'left' | 'right' }) {
  return (
    <button
      onClick={props.onClick}
      type="button"
      className="input-reset pointer w-100 b--none bg-white pa2 lh-solid link"
    >
      <i className={`fa fa-chevron-${props.dir} silver`} />
    </button>
  );
}



// WEBPACK FOOTER //
// ./src/containers/search/SearchForm/DatePicker.js